import { Link } from "react-router-dom";
import { ArrowRight, Link2, Hourglass, Network, Globe } from "lucide-react";
import { HERO_IMG } from "@/lib/constants";
import { PublicNav } from "@/components/PublicNav";

const FEATURES = [
  { icon: Link2, title: "Living cross-links", text: "Type [[Any Name]] and your wiki weaves itself. Backlinks appear on their own." },
  { icon: Hourglass, title: "Chronicle timeline", text: "Every event, ordered across ages, eras and calendars of your own design." },
  { icon: Network, title: "Relationship maps", text: "Allies, rivals, lovers and blood-ties drawn as a living constellation." },
  { icon: Globe, title: "Share the codex", text: "Private by default. One switch opens a read-only link for readers & editors." },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="landing-page">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-canvas via-canvas/90 to-canvas/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-canvas via-transparent to-transparent" />
      </div>
      <header className="relative z-10 flex items-center justify-between px-6 md:px-14 py-6">
        <span className="font-display text-2xl text-amber-100 tracking-wide" data-testid="brand-logo">Lore<span className="text-amber-500">ly</span></span>
        <div className="flex items-center gap-3">
          <PublicNav />
        </div>
      </header>
      <main className="relative z-10 px-6 md:px-14 pt-16 md:pt-28 pb-24 max-w-6xl">
        <p className="rise font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-6">For novelists, game-masters & fanfic writers</p>
        <h1 className="rise font-display text-5xl sm:text-6xl lg:text-7xl font-semibold text-amber-50 leading-[1.02] max-w-3xl" style={{ animationDelay: "80ms" }}>
          Every world deserves <em className="text-amber-400 font-normal">its own codex.</em>
        </h1>
        <p className="rise mt-8 max-w-xl text-base md:text-lg text-slate-400 leading-relaxed" style={{ animationDelay: "160ms" }}>
          Build interlinked wikis for your original universes and the fandoms you love: characters, kingdoms, factions, lore and the long chronicle of events.
        </p>
        <div className="rise mt-10 flex flex-wrap gap-4" style={{ animationDelay: "240ms" }}>
          <Link to="/register" data-testid="hero-get-started-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-onaccent font-semibold hover:bg-amber-400 transition-colors">
            Start your first world <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/login" data-testid="hero-login-btn" className="inline-flex items-center rounded-full border border-line px-7 py-3.5 text-slate-200 hover:border-amber-500/40 transition-colors">
            I have an account
          </Link>
        </div>
        <div className="mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line/60 rounded-2xl overflow-hidden border border-line">
          {FEATURES.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="rise bg-canvas/90 backdrop-blur-xl p-6 hover:bg-panel transition-colors" style={{ animationDelay: `${320 + i * 70}ms` }}>
              <Icon className="w-5 h-5 text-amber-500 mb-4" />
              <h3 className="font-display text-xl text-amber-100 mb-2">{title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
